// Iter-282c — Diversity Snapshot card for the Admin Dashboard.
// Reads /api/admin/talents/diversity-breakdown which returns:
//   { breakdown: { <canonical_ethnicity>: count, ... },
//     not_disclosed: <int>,           // NULLs (no value set)
//     non_canonical: <int>,           // sanity counter; expected 0
//     audit_remapped_count: <int>,    // talents whose original free-text was migrated
//     canonical_options: [...]
//   }
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Users, BarChart3, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const PALETTE = [
  'bg-amber-500', 'bg-emerald-500', 'bg-cyan-500', 'bg-rose-500',
  'bg-indigo-500', 'bg-orange-500', 'bg-fuchsia-500', 'bg-lime-500',
  'bg-sky-500', 'bg-violet-500',
];

const DiversitySnapshotCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/talents/diversity-breakdown');
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load diversity snapshot');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const total = data
    ? Object.values(data.breakdown).reduce((a, b) => a + b, 0) + (data.not_disclosed || 0)
    : 0;

  const downloadCsv = () => {
    if (!data) return;
    const rows = [['ethnicity', 'count', 'percent']];
    Object.entries(data.breakdown).forEach(([k, v]) => {
      const pct = total ? ((v / total) * 100).toFixed(1) : '0.0';
      rows.push([k, v, `${pct}%`]);
    });
    const pct = total ? ((data.not_disclosed / total) * 100).toFixed(1) : '0.0';
    rows.push(['Not Specified', data.not_disclosed, `${pct}%`]);
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'diversity_snapshot.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card data-testid="admin-diversity-snapshot-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="w-4 h-4 text-cyan-600" />
          Diversity Snapshot
          <Badge className="bg-gray-100 text-gray-700 border-gray-200 text-[10px] ml-1">
            Admin only
          </Badge>
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 ml-1" />}
          <Button
            variant="outline" size="sm" className="ml-auto h-7 text-xs"
            onClick={downloadCsv} disabled={!data}
            data-testid="diversity-snapshot-csv"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> CSV
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!data ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
              <Users className="w-3.5 h-3.5" />
              <span>{total.toLocaleString()} active talent profiles</span>
              {data.audit_remapped_count > 0 && (
                <span className="ml-2 text-gray-400">
                  · {data.audit_remapped_count} migrated from legacy free-text
                </span>
              )}
            </div>

            {/* Stacked bar */}
            <div className="flex h-3 rounded-full overflow-hidden mb-4 bg-gray-100">
              {Object.entries(data.breakdown).map(([k, v], i) => {
                if (!v) return null;
                const pct = total ? (v / total) * 100 : 0;
                return (
                  <div
                    key={k}
                    className={`${PALETTE[i % PALETTE.length]} h-3`}
                    style={{ width: `${pct}%` }}
                    title={`${k}: ${v} (${pct.toFixed(1)}%)`}
                    data-testid={`diversity-bar-${k.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                  />
                );
              })}
              {data.not_disclosed > 0 && (
                <div
                  className="bg-gray-300 h-3"
                  style={{ width: `${(data.not_disclosed / total) * 100}%` }}
                  title={`Not Specified: ${data.not_disclosed}`}
                  data-testid="diversity-bar-not-specified"
                />
              )}
            </div>

            {/* Per-row legend */}
            <ul className="space-y-1.5 text-sm">
              {Object.entries(data.breakdown).map(([k, v], i) => {
                const pct = total ? ((v / total) * 100).toFixed(1) : '0.0';
                return (
                  <li
                    key={k}
                    className="flex items-center justify-between"
                    data-testid={`diversity-row-${k.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                  >
                    <span className="flex items-center gap-2">
                      <span className={`inline-block w-2.5 h-2.5 rounded-sm ${PALETTE[i % PALETTE.length]}`} />
                      <span className="text-gray-700">{k}</span>
                    </span>
                    <span className="text-gray-500 tabular-nums">
                      {v.toLocaleString()} <span className="text-gray-400">({pct}%)</span>
                    </span>
                  </li>
                );
              })}
              <li
                className="flex items-center justify-between pt-1.5 border-t border-gray-100"
                data-testid="diversity-row-not-specified"
              >
                <span className="flex items-center gap-2">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gray-300" />
                  <span className="text-gray-500">Not Specified</span>
                </span>
                <span className="text-gray-500 tabular-nums">
                  {(data.not_disclosed || 0).toLocaleString()}{' '}
                  <span className="text-gray-400">
                    ({total ? ((data.not_disclosed / total) * 100).toFixed(1) : '0.0'}%)
                  </span>
                </span>
              </li>
            </ul>

            <p className="mt-3 text-[11px] text-gray-400 leading-snug">
              Talent self-reports ethnicity from a standardized 10-value vocabulary.
              Counts include "Prefer Not to Answer" (an explicit choice) and
              "Not Specified" (no value set). Use the CSV export for compliance reports.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DiversitySnapshotCard;
